import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { History } from "lucide-react";
import { articlesAPI } from "../services/api";
import { useToasts } from "../hooks/useToasts";
import Button from "../components/Button";
import Filter from "../components/Filter";
import "./UserSpace.css";

const CONTENT_TYPE_LABELS = {
  lesson: "Lessons",
  note: "Notes",
  flashcard: "Flashcards",
};

const formatDateTime = (value) => {
  if (!value) {
    return "N/A";
  }

  return new Date(value).toLocaleString("ro-RO", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getContentBadgeLabel = (article) => {
  if (article.contentType === "lesson") return "L";
  if (article.contentType === "flashcard") return "F";
  return "N";
};

const getContentBadgeClassName = (article) => {
  if (article.contentType === "lesson") {
    return "userspace-content-badge userspace-content-badge-lesson";
  }
  if (article.contentType === "flashcard") {
    return "userspace-content-badge userspace-content-badge-flashcard";
  }
  return "userspace-content-badge userspace-content-badge-note";
};

const getBodyPreviewText = (article) => {
  if (article.contentType === "flashcard") {
    const question = article.chapter || "";
    const answer = article.body || "";
    const questionPreview =
      question.length > 50 ? `${question.substring(0, 50)}...` : question;
    const answerPreview =
      answer.length > 50 ? `${answer.substring(0, 50)}...` : answer;

    return `Q: ${questionPreview} | A: ${answerPreview}`;
  }

  if (!article.body) {
    return "";
  }

  return article.body.length > 100
    ? `${article.body.substring(0, 100)}...`
    : article.body;
};

const isEffectivelyDeactivated = (article) =>
  Boolean(
    article?.effectiveIsDeactivatedByAdmin ?? article?.isDeactivatedByAdmin,
  );

const buildModalBodyHtml = (article) => {
  if (article.contentType === "flashcard") {
    return `Q: ${article.chapter || ""}<br><br>A: ${article.body || ""}`;
  }

  return (article.body || "").replace(/\n/g, "<br>");
};

const UserSpace = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const folderName = searchParams.get("folder");
  const contentType = searchParams.get("type");
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [visibilityFilter, setVisibilityFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [quickDate, setQuickDate] = useState("");
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const { addToast } = useToasts();

  const filteredArticles = useMemo(() => {
    return articles.filter((article) => {
      const statusMatch =
        statusFilter === "all" ||
        (statusFilter === "active" && !isEffectivelyDeactivated(article)) ||
        (statusFilter === "deactivated" && isEffectivelyDeactivated(article));

      const visibilityMatch =
        visibilityFilter === "all" ||
        (visibilityFilter === "private" && !article.isPublic) ||
        (visibilityFilter === "public" && article.isPublic);

      const typeMatch = !contentType || article.contentType === contentType;

      let dateMatch = true;
      if (article.createdAt) {
        const articleDate = new Date(article.createdAt);

        if (quickDate) {
          const now = new Date();

          if (quickDate === "today") {
            dateMatch =
              articleDate.getFullYear() === now.getFullYear() &&
              articleDate.getMonth() === now.getMonth() &&
              articleDate.getDate() === now.getDate();
          } else if (quickDate === "week") {
            const sevenDaysAgo = new Date(now);
            sevenDaysAgo.setDate(now.getDate() - 7);
            dateMatch = articleDate >= sevenDaysAgo;
          } else if (quickDate === "month") {
            dateMatch =
              articleDate.getFullYear() === now.getFullYear() &&
              articleDate.getMonth() === now.getMonth();
          }
        } else if (dateFilter) {
          dateMatch = articleDate.toISOString().slice(0, 10) === dateFilter;
        }
      }

      return statusMatch && visibilityMatch && dateMatch && typeMatch;
    });
  }, [articles, contentType, dateFilter, quickDate, statusFilter, visibilityFilter]);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);

        let response;
        if (folderName) {
          response = await articlesAPI.getArticlesByFolder(
            folderName,
            contentType || undefined,
          );
          setCurrentFolder(folderName);
        } else {
          response = await articlesAPI.getMyArticles();
          setCurrentFolder(null);
        }

        setArticles(
          response.data.articles.filter(
            (article) =>
              article.body !== "Folder placeholder" &&
              article.body !== "Subfolder placeholder",
          ),
        );
      } catch (error) {
        addToast(error.message || "Failed to load articles", "error-warning");
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [addToast, contentType, folderName]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        navigate("/userfolders");
      }
    };

    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [navigate]);

  const handleResetFilters = () => {
    setStatusFilter("all");
    setVisibilityFilter("all");
    setDateFilter("");
    setQuickDate("");
  };

  const openBodyModal = (article) => {
    const modal = document.createElement("div");
    modal.className = "body-modal";
    modal.innerHTML = `
      <div class="body-modal-content">
        <span class="body-modal-close">&times;</span>
        <div class="body-modal-text">${buildModalBodyHtml(article)}</div>
      </div>
    `;

    const closeModal = () => {
      if (modal.parentNode) {
        document.body.removeChild(modal);
      }
      document.removeEventListener("keydown", handleModalEsc);
    };

    const handleModalEsc = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        closeModal();
      }
    };

    modal.querySelector(".body-modal-close").onclick = closeModal;
    modal.onclick = (event) => {
      if (event.target === modal) {
        closeModal();
      }
    };

    document.body.appendChild(modal);
    document.addEventListener("keydown", handleModalEsc);
  };

  const hasActiveFilters =
    statusFilter !== "all" ||
    visibilityFilter !== "all" ||
    dateFilter ||
    quickDate;

  const shouldShowViewButton =
    contentType === "note" ||
    contentType === "lesson" ||
    contentType === "flashcard";

  const historyColumnVisible = true;
  const tableColumnCount = historyColumnVisible ? 9 : 8;
  const pageTitle = currentFolder || "All Articles";
  const contentTypeTitle = contentType
    ? ` - ${CONTENT_TYPE_LABELS[contentType] || "Content"}`
    : "";

  return (
    <div className="form-content-frame table-page">
      <div className="userspace-header">
        <div className="userspace-title-group">
          <h1>
            {pageTitle}
            {contentTypeTitle}
          </h1>
          <div className="userspace-count-badge">{filteredArticles.length}</div>
        </div>
        <Button
          className={`button-reset${
            hasActiveFilters ? " button-reset-visible" : ""
          }`}
          onClick={handleResetFilters}
        >
          Reset Filters
        </Button>
      </div>

      <div className="userspace-filters-section">
        <div className="userspace-filter-group userspace-time-filters">
          <input
            type="date"
            value={dateFilter}
            onChange={(event) => {
              setDateFilter(event.target.value);
              setQuickDate("");
            }}
          />
          <button
            type="button"
            className={`userspace-btn-filter ${
              quickDate === "today" ? "active" : ""
            }`}
            onClick={() => {
              setQuickDate("today");
              setDateFilter("");
            }}
          >
            Today
          </button>
          <button
            type="button"
            className={`userspace-btn-filter ${
              quickDate === "week" ? "active" : ""
            }`}
            onClick={() => {
              setQuickDate("week");
              setDateFilter("");
            }}
          >
            Week
          </button>
          <button
            type="button"
            className={`userspace-btn-filter ${
              quickDate === "month" ? "active" : ""
            }`}
            onClick={() => {
              setQuickDate("month");
              setDateFilter("");
            }}
          >
            Month
          </button>
        </div>
        <Button
          className={`button-view-myspace${
            shouldShowViewButton ? "" : " userspace-view-button-hidden"
          }`}
          onClick={() => {
            if (contentType === "note") {
              navigate("/all-notes");
            } else if (contentType === "lesson") {
              navigate("/all-lessons");
            } else if (contentType === "flashcard") {
              navigate("/all-flashcards");
            }
          }}
        >
          {contentType === "note"
            ? "View Notes"
            : contentType === "lesson"
              ? "View Lessons"
              : contentType === "flashcard"
                ? "View Flashcards"
                : ""}
        </Button>
      </div>

      <div className="userspace-table-container">
        <table className="userspace-table">
          <thead>
            <tr>
              <th className="header-cell">
                <div className="header-title">No</div>
              </th>
              <th className="header-cell">
                <div className="header-title">Title</div>
              </th>
              <th className="header-cell">
                <div className="header-title">Author</div>
              </th>
              <th className="header-cell">
                <div className="header-title">Lesson</div>
              </th>
              <th className="header-cell">
                <div className="header-title">Body</div>
              </th>
              <th className="header-cell">
                <div className="header-title">Status</div>
                <Filter
                  className="header-filter"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option value="all">All</option>
                  <option value="active">Active</option>
                  <option value="deactivated">Deactivated</option>
                </Filter>
              </th>
              <th className="header-cell">
                <div className="header-title">Visibility</div>
                <Filter
                  className="header-filter"
                  value={visibilityFilter}
                  onChange={(event) => setVisibilityFilter(event.target.value)}
                >
                  <option value="all">All</option>
                  <option value="private">Private</option>
                  <option value="public">Public</option>
                </Filter>
              </th>
              <th className="header-cell">
                <div className="header-title">Created</div>
              </th>
              {historyColumnVisible && (
                <th className="header-cell">
                  <div className="header-title">History</div>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={tableColumnCount} className="userspace-empty-message">
                  Loading...
                </td>
              </tr>
            ) : filteredArticles.length === 0 ? (
              <tr>
                <td colSpan={tableColumnCount} className="userspace-empty-message">
                  No articles found
                </td>
              </tr>
            ) : (
              filteredArticles.map((article, index) => (
                <React.Fragment key={article._id}>
                  <tr>
                    <td>
                      <div className="userspace-index-cell">
                        <span>{index + 1}</span>
                        <span className={getContentBadgeClassName(article)}>
                          {getContentBadgeLabel(article)}
                        </span>
                      </div>
                    </td>
                    <td className="title-cell">{article.title}</td>
                    <td>{article.author}</td>
                    <td>
                      {article.contentType === "flashcard"
                        ? article.linkedLessonChapter || article.lessonId?.chapter || ""
                        : article.chapter || ""}
                    </td>
                    <td className="body-cell">
                      <div
                        className="body-preview"
                        onClick={() => openBodyModal(article)}
                        title="Click to view full text"
                      >
                        {getBodyPreviewText(article)}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`status-badge status-${
                          isEffectivelyDeactivated(article)
                            ? "deactivated"
                            : "active"
                        }`}
                      >
                        {isEffectivelyDeactivated(article)
                          ? "Deactivated by Admin"
                          : "Active"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`visibility-badge visibility-${
                          article.isPublic ? "public" : "private"
                        }`}
                      >
                        {article.isPublic ? "Public" : "Private"}
                      </span>
                    </td>
                    <td>{formatDateTime(article.createdAt)}</td>
                    {historyColumnVisible && (
                      <td>
                        <button
                          className="icon-button icon-button-gray"
                          onClick={() => {
                            setExpandedHistoryId(
                              expandedHistoryId === article._id
                                ? null
                                : article._id,
                            );
                          }}
                          title={`History (${article.history?.length || 0})`}
                        >
                          <History size={16} />
                        </button>
                      </td>
                    )}
                  </tr>
                  {historyColumnVisible && expandedHistoryId === article._id && (
                    <tr>
                      <td colSpan={tableColumnCount} className="edit-history-cell">
                        <div className="edit-history-container">
                          <div className="userspace-history-title">
                            Edit History
                          </div>
                          {article.history && article.history.length > 0 ? (
                            article.history
                              .slice()
                              .reverse()
                              .map((historyItem, historyIndex) => (
                                <div
                                  key={historyIndex}
                                  className="userspace-history-item"
                                >
                                  {historyItem.oldTitle !== null && (
                                    <div>Title: {historyItem.oldTitle}</div>
                                  )}
                                  {historyItem.oldAuthor !== null && (
                                    <div>Author: {historyItem.oldAuthor}</div>
                                  )}
                                  {historyItem.oldChapter !== null && (
                                    <div>Lesson: {historyItem.oldChapter}</div>
                                  )}
                                  {historyItem.oldBody !== null && (
                                    <div>Body: {historyItem.oldBody}</div>
                                  )}
                                  <div>
                                    Modified At:{" "}
                                    {formatDateTime(historyItem.modifiedAt)}
                                  </div>
                                  <div>
                                    Modified By: {historyItem.modifierId}
                                  </div>
                                </div>
                              ))
                          ) : (
                            <div>No edit history available</div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserSpace;
